import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'hooks';

import { Button, Select } from '~components';
import Modal from '~components/Modal';
import SelectLazy from '~components/form-fields/inputs/SelectLazy';

import { getAll as getClients } from '~main/actions/clients';
import { selectLookupClients } from '~main/selectors/clients/clients';

import { useGetTemplatesQuery } from '~forms/actions/templates';

interface ComponentProps {
  defaultClientId: number;
  isOpen: boolean;
  onCancel: () => void;
}

const ClientFormFillModal: React.FC<ComponentProps> = ({ defaultClientId, isOpen, onCancel }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data: templates, isFetching: isTemplatesFetching } = useGetTemplatesQuery();

  const [templateId, setTemplateId] = useState<string | null>(null);
  const [clientId, setClientId] = useState<number | null>(defaultClientId);

  // Reset selection whenever the modal is reopened, and make sure the pre-seeded client's
  // name is resolvable in the SelectLazy options (it only lazy-loads on focus otherwise).
  useEffect(() => {
    if (isOpen) {
      setTemplateId(null);
      setClientId(defaultClientId);
      dispatch(getClients());
    }
  }, [isOpen, defaultClientId, dispatch]);

  const templateOptions = (templates ?? [])
    .filter((tpl) => tpl.status === 'published')
    .map((tpl) => ({ value: tpl.id, label: tpl.title }));

  const handleTemplateChange = (_name: string, value: string | null) => setTemplateId(value);
  const handleClientChange = (_name: string, value: number | null) => setClientId(value);

  const handleConfirm = () => {
    if (templateId === null || clientId === null) return;
    navigate(`/forms/templates/${templateId}/fill?clientId=${clientId}`);
    onCancel();
  };

  const setIsOpen = (open: boolean) => {
    if (!open) onCancel();
  };

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen} header="Fill Form" includeFooter={false} backdrop="static">
      <div className="d-flex flex-column gap-2">
        <label htmlFor="client-form-fill-template" className="fw-semibold">
          Form
        </label>
        <Select
          id="client-form-fill-template"
          name="template_id"
          value={templateId ?? undefined}
          placeholder={isTemplatesFetching ? 'Loading forms…' : 'Select a form…'}
          options={templateOptions}
          disabled={isTemplatesFetching}
          onChange={handleTemplateChange}
        />
        <label htmlFor="client-form-fill-client" className="fw-semibold">
          Client
        </label>
        <SelectLazy
          type="select-lazy"
          id="client-form-fill-client"
          name="client_id"
          value={clientId ?? undefined}
          placeholder="Search clients…"
          selectorOptions={selectLookupClients}
          selectorActions={getClients}
          manualReload
          onChange={handleClientChange}
        />
        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button
            id="client-form-fill-cancel"
            data-cy="client-form-fill-cancel"
            color="info"
            outline
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            id="client-form-fill-confirm"
            data-cy="client-form-fill-confirm"
            color="success"
            onClick={handleConfirm}
            disabled={templateId === null || clientId === null}
          >
            Fill Form
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ClientFormFillModal;
